import { all, call, put, takeEvery } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import { t } from 'ttag'
import { addNotification } from '../../../common/notifications/NotificationActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import api from '../../../services/api.services'
import ApiClient from '../../../services/ApiClient'
import { getErrorMsg } from '../../../common/helpers/translations'
import {
  getCompanySsoConfigs,
  getCompanySsoConfigsSuccess,
  getCompanySsoConfigsFail,
  getCompanySsoConfig,
  getCompanySsoConfigSuccess,
  getCompanySsoConfigFail,
  createCompanySsoConfig,
  createCompanySsoConfigSuccess,
  createCompanySsoConfigFail,
  updateCompanySsoConfig,
  updateCompanySsoConfigSuccess,
  updateCompanySsoConfigFail,
  deleteCompanySsoConfig,
  deleteCompanySsoConfigSuccess,
  deleteCompanySsoConfigFail,
  enableScim,
  enableScimSuccess,
  enableScimFail,
  disableScim,
  disableScimSuccess,
  disableScimFail,
  regenerateScimToken,
  regenerateScimTokenSuccess,
  regenerateScimTokenFail,
  getCompanySsoSettings,
  getCompanySsoSettingsSuccess,
  getCompanySsoSettingsFail,
  updateCompanySsoSettings,
  updateCompanySsoSettingsSuccess,
  updateCompanySsoSettingsFail,
} from './SsoAdminActions'

/**
 * Saga: fetch all SSO configs for a company (list view).
 */
function* sagaGetCompanySsoConfigs({ payload }: any) {
  try {
    const { companyId } = payload
    const response: any = yield call(api.auth.getCompanySsoConfigs, { companyId })
    yield put({ type: String(getCompanySsoConfigsSuccess), payload: response })
  } catch (e: any) {
    yield put({ type: String(getCompanySsoConfigsFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('getCompanySsoConfigs'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: fetch a single SSO config by ssoConfigId (detail view).
 */
function* sagaGetCompanySsoConfig({ payload }: any) {
  try {
    const { ssoConfigId } = payload
    const response: any = yield call(api.auth.getCompanySsoConfig, { ssoConfigId })
    yield put({ type: String(getCompanySsoConfigSuccess), payload: response })
  } catch (e: any) {
    yield put({ type: String(getCompanySsoConfigFail) })
    if (e.status !== 404) {
      const notification: INotification = {
        type: 'warning',
        content: getErrorMsg('getCompanySsoConfig'),
      }
      yield put({ type: String(addNotification), payload: notification })
    }
  }
}

/**
 * Saga: create a new SSO config.
 */
function* sagaCreateCompanySsoConfig({ payload }: any) {
  try {
    const response: any = yield call(api.auth.createCompanySsoConfig, payload)
    yield put({ type: String(createCompanySsoConfigSuccess), payload: response })

    const notification: INotification = {
      type: 'success',
      content: t`SSO configuration created successfully.`,
    }
    yield put({ type: String(addNotification), payload: notification })
  } catch (e: any) {
    yield put({ type: String(createCompanySsoConfigFail) })
    const notification: INotification = {
      type: 'warning',
      content:
        e.message && e.message !== 'No message has been provided by the server'
          ? e.message
          : getErrorMsg('createCompanySsoConfig'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: update an existing SSO config.
 */
function* sagaUpdateCompanySsoConfig({ payload }: any) {
  try {
    yield call(api.auth.updateCompanySsoConfig, payload)
    yield put({ type: String(updateCompanySsoConfigSuccess) })

    const notification: INotification = {
      type: 'success',
      content: t`SSO configuration saved successfully.`,
    }
    yield put({ type: String(addNotification), payload: notification })

    // Navigate back to the SSO list
    yield put(push('/admin/sso'))
  } catch (e: any) {
    yield put({ type: String(updateCompanySsoConfigFail) })
    const notification: INotification = {
      type: 'warning',
      content:
        e.message && e.message !== 'No message has been provided by the server'
          ? e.message
          : getErrorMsg('updateCompanySsoConfig'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: delete an SSO config by ssoConfigId.
 */
function* sagaDeleteCompanySsoConfig({ payload }: any) {
  try {
    const { ssoConfigId, companyId } = payload
    yield call(api.auth.deleteCompanySsoConfig, { ssoConfigId })
    yield put({ type: String(deleteCompanySsoConfigSuccess) })

    const notification: INotification = {
      type: 'success',
      content: t`SSO configuration deleted.`,
    }
    yield put({ type: String(addNotification), payload: notification })

    // Navigate back to the SSO list
    yield put(push('/admin/sso'))
  } catch (e: any) {
    yield put({ type: String(deleteCompanySsoConfigFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('deleteCompanySsoConfig'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: enable SCIM for an SSO config.
 */
function* sagaEnableScim({ payload }: any) {
  try {
    const response: any = yield call(api.auth.enableScim, payload)
    yield put({ type: String(enableScimSuccess), payload: response })

    const notification: INotification = {
      type: 'success',
      content: t`SCIM provisioning enabled.`,
    }
    yield put({ type: String(addNotification), payload: notification })
  } catch (e: any) {
    yield put({ type: String(enableScimFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('enableScim'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: disable SCIM for an SSO config.
 */
function* sagaDisableScim({ payload }: any) {
  try {
    yield call(api.auth.disableScim, payload)
    yield put({ type: String(disableScimSuccess) })

    const notification: INotification = {
      type: 'success',
      content: t`SCIM provisioning disabled.`,
    }
    yield put({ type: String(addNotification), payload: notification })
  } catch (e: any) {
    yield put({ type: String(disableScimFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('disableScim'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: regenerate SCIM bearer token.
 */
function* sagaRegenerateScimToken({ payload }: any) {
  try {
    const response: any = yield call(api.auth.regenerateScimToken, payload)
    yield put({ type: String(regenerateScimTokenSuccess), payload: response })

    const notification: INotification = {
      type: 'success',
      content: t`SCIM token regenerated. Copy it now — it won't be shown again.`,
    }
    yield put({ type: String(addNotification), payload: notification })
  } catch (e: any) {
    yield put({ type: String(regenerateScimTokenFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('regenerateScimToken'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

/**
 * Saga: fetch company-level SSO settings.
 */
function* sagaGetCompanySsoSettings({ payload }: any) {
  try {
    const { companyId } = payload
    const response: any = yield call(ApiClient.post as any, 'getCompanySsoSettings', { companyId })
    yield put({ type: String(getCompanySsoSettingsSuccess), payload: response })
  } catch (e: any) {
    yield put({ type: String(getCompanySsoSettingsFail) })
  }
}

/**
 * Saga: update company-level SSO settings.
 */
function* sagaUpdateCompanySsoSettings({ payload }: any) {
  try {
    const response: any = yield call(ApiClient.post as any, 'updateCompanySsoSettings', payload)
    yield put({ type: String(updateCompanySsoSettingsSuccess), payload: response })
    const notification: INotification = {
      type: 'success',
      content: t`Settings updated.`,
    }
    yield put({ type: String(addNotification), payload: notification })
  } catch (e: any) {
    yield put({ type: String(updateCompanySsoSettingsFail) })
    const notification: INotification = {
      type: 'warning',
      content: getErrorMsg('updateCompanySsoSettings'),
    }
    yield put({ type: String(addNotification), payload: notification })
  }
}

export function* ssoAdminSagas() {
  yield all([
    takeEvery(String(getCompanySsoConfigs), sagaGetCompanySsoConfigs),
    takeEvery(String(getCompanySsoConfig), sagaGetCompanySsoConfig),
    takeEvery(String(createCompanySsoConfig), sagaCreateCompanySsoConfig),
    takeEvery(String(updateCompanySsoConfig), sagaUpdateCompanySsoConfig),
    takeEvery(String(deleteCompanySsoConfig), sagaDeleteCompanySsoConfig),
    takeEvery(String(enableScim), sagaEnableScim),
    takeEvery(String(disableScim), sagaDisableScim),
    takeEvery(String(regenerateScimToken), sagaRegenerateScimToken),
    takeEvery(String(getCompanySsoSettings), sagaGetCompanySsoSettings),
    takeEvery(String(updateCompanySsoSettings), sagaUpdateCompanySsoSettings),
  ])
}
